import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Central pública de materiais Sollute.
 *
 * Materiais são gerenciados em /app/admin/downloads pelo administrador.
 * Esta página consome `/api/downloads` (apenas registros com status="published")
 * e só atende usuários com plano pago (gating no backend → HTTP 402).
 */
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const COLOR_BY_EXT = {
  pdf:  "text-rose-700",
  doc:  "text-sky-700",
  docx: "text-sky-700",
  xls:  "text-emerald-800",
  xlsx: "text-emerald-800",
  ppt:  "text-orange-700",
  pptx: "text-orange-700",
  png:  "text-violet-700",
  jpg:  "text-violet-700",
  jpeg: "text-violet-700",
  zip:  "text-amber-700",
};

function PaywallNotice() {
  return (
    <div
      className="card mt-4 border-amber-200 bg-amber-50"
      data-testid="downloads-paywall"
    >
      <div className="flex items-start gap-3">
        <i className="fa-solid fa-lock text-amber-700 text-lg mt-0.5"></i>
        <div className="text-sm text-amber-900">
          <strong>Recurso exclusivo do plano completo.</strong> Os materiais
          oficiais Sollute (manuais, templates e apresentações) estão disponíveis
          apenas para assinantes ativos.{" "}
          <Link to="/app/planos" className="underline font-semibold" data-testid="downloads-upgrade-link">
            Conheça os planos
          </Link>
          .
        </div>
      </div>
    </div>
  );
}

export default function Downloads() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paywall, setPaywall] = useState(false);
  const [counts, setCounts] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setPaywall(false);
    try {
      const { data } = await api.get("/downloads");
      setItems(data);
    } catch (err) {
      const status = err.response?.status;
      if (status === 402) {
        setPaywall(true);
      } else {
        setError(err.response?.data?.detail || err.message);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem("sollute_downloads_counts");
      setCounts(raw ? JSON.parse(raw) : {});
    } catch {
      setCounts({});
    }
  }, []);

  const bumpCount = (id) => {
    setCounts((prev) => {
      const next = { ...prev, [id]: (prev[id] || 0) + 1 };
      try { localStorage.setItem("sollute_downloads_counts", JSON.stringify(next)); } catch { /* private mode */ }
      return next;
    });
  };

  // Agrupa por categoria preservando a ordem retornada pela API
  const grouped = useMemo(() => {
    const order = [];
    const byCat = new Map();
    for (const it of items) {
      const cat = it.category || "Outros";
      if (!byCat.has(cat)) { byCat.set(cat, []); order.push(cat); }
      byCat.get(cat).push(it);
    }
    return order.map((c) => ({ category: c, items: byCat.get(c) }));
  }, [items]);

  return (
    <div className="p-8 lg:p-12 max-w-6xl mx-auto" data-testid="downloads-page">
      <div className="mb-10">
        <div className="overline mb-2">Material de apoio</div>
        <h1 className="font-display text-4xl lg:text-5xl font-extrabold tracking-tight">Downloads</h1>
        <p className="text-slate-600 mt-3 max-w-2xl">
          Manuais, apresentações e templates oficiais Sollute para apoiar a aplicação do
          questionário NR-01 em campo, treinamentos internos e reuniões com o CIPA.
        </p>
      </div>

      {loading && (
        <div className="card text-center text-slate-500 py-12" data-testid="downloads-loading">
          <i className="fa-solid fa-spinner fa-spin text-2xl mb-3"></i>
          <p className="text-sm">Carregando materiais…</p>
        </div>
      )}

      {!loading && paywall && <PaywallNotice />}

      {!loading && error && (
        <div className="card border-rose-200 bg-rose-50 text-rose-900 text-sm" data-testid="downloads-error">
          <i className="fa-solid fa-triangle-exclamation mr-2"></i>{error}
        </div>
      )}

      {!loading && !paywall && !error && items.length === 0 && (
        <div className="card text-center text-slate-500 py-12" data-testid="downloads-empty">
          <i className="fa-solid fa-folder-open text-3xl mb-3 text-slate-300"></i>
          <p className="text-sm">
            Nenhum material publicado no momento.
            {user?.role === "admin" && (
              <>
                {" "}
                <Link to="/app/admin/downloads" className="text-emerald-700 underline">
                  Faça upload de um arquivo
                </Link>{" "}
                para começar.
              </>
            )}
          </p>
        </div>
      )}

      {!loading && !paywall && !error && grouped.map(({ category, items: list }) => (
        <section key={category} className="mb-10" data-testid={`downloads-cat-${category}`}>
          <h2 className="overline text-emerald-800 mb-3">{category}</h2>
          <div className="space-y-4">
            {list.map((r) => {
              const url = `${BACKEND}/api/downloads/${r.id}/file`;
              const color = COLOR_BY_EXT[r.ext] || "text-emerald-800";
              return (
                <article
                  key={r.id}
                  data-testid={`resource-card-${r.id}`}
                  className="card border-slate-200 hover:border-emerald-300 transition-colors"
                >
                  <div className="flex flex-col md:flex-row md:items-start gap-5">
                    <div
                      className="w-14 h-14 shrink-0 flex items-center justify-center border border-emerald-200"
                      style={{ background: "var(--so-primary-soft)", color: "var(--so-primary)" }}
                    >
                      <i className={`fa-solid ${r.icon || "fa-file"} text-2xl`}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <span className="overline text-emerald-700 text-[10px]">{r.category}</span>
                        {r.tag && (
                          <span className="text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 bg-[#8BC53F]/20 text-emerald-900 border border-[#8BC53F]/40">
                            {r.tag}
                          </span>
                        )}
                      </div>
                      <h2 className="font-display text-xl lg:text-2xl font-extrabold text-slate-900 leading-snug mb-2">
                        {r.title}
                      </h2>
                      {r.description && (
                        <p className="text-sm text-slate-600 leading-relaxed mb-4">{r.description}</p>
                      )}

                      <a
                        href={url}
                        target="_blank"
                        rel="noreferrer"
                        download={r.original_filename}
                        onClick={() => bumpCount(r.id)}
                        data-testid={`download-${r.id}`}
                        className="group inline-flex items-center gap-3 p-3 border border-slate-200 hover:border-emerald-700 hover:bg-emerald-50 transition-colors"
                      >
                        <i className={`fa-solid ${r.icon || "fa-file"} text-2xl ${color}`}></i>
                        <div className="min-w-0 flex-1">
                          <div className="font-semibold text-slate-900 text-sm">
                            Baixar arquivo
                          </div>
                          <div className="text-[11px] text-slate-500 font-mono uppercase tracking-wider">
                            .{r.ext} · {r.size_human}
                            {counts[r.id] ? <span className="ml-2 text-emerald-700">· acessado {counts[r.id]}x</span> : null}
                          </div>
                        </div>
                        <i className="fa-solid fa-download text-slate-400 group-hover:text-emerald-700 transition-colors"></i>
                      </a>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </section>
      ))}

      <div className="card mt-8 border-slate-200 bg-slate-50">
        <div className="flex items-start gap-3">
          <i className="fa-solid fa-circle-info text-emerald-700 text-lg mt-0.5"></i>
          <div className="text-sm text-slate-600">
            <strong className="text-slate-900">Material atualizado pela Sollute Ocupacional.</strong>{" "}
            Sentiu falta de algum template (PGR, PCMSO, ASO, checklist NR-05)?{" "}
            <a
              href="mailto:contato@sollute.com.br?subject=Sugest%C3%A3o%20de%20novo%20download"
              className="text-emerald-800 underline hover:text-emerald-900"
              data-testid="downloads-suggest-link"
            >
              Envie uma sugestão
            </a>{" "}
            — incluímos no próximo ciclo de atualização.
          </div>
        </div>
      </div>
    </div>
  );
}
